import { updateBrandingTheme } from 'flow/actions/brandingV2-actions';
import homeClient from 'flow/api-clients/home-client';
import { addHttpsIfNoProtocol } from 'flow/utils';
import { getGeoNexBaseUrl } from 'flow/utils/geonex-api-utils';

// =============================================================================
// STATE MANAGEMENT
// =============================================================================

let API_BRAND = null;
let API_LOGOS = null;

let ENVIRONMENT_BRAND = null;
let ENVIRONMENT_LOGOS = null;
let ENVIRONMENT_THEME = null;

export const setBrandFromAPI = (APIanswer) => {
    API_BRAND = createBrandFromApiBrand(APIanswer);
};

export const setLogosFromAPI = (APIanswer) => {
    API_LOGOS = APIanswer;
};

export const setEnvironmentBrand = (environmentAPIAnswer) => {
    ENVIRONMENT_BRAND = createBrandFromApiBrand(environmentAPIAnswer);
    ENVIRONMENT_THEME = environmentAPIAnswer.theme;
};

export const setEnvironmentLogos = (logos) => {
    ENVIRONMENT_LOGOS = logos;
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const getPasswordActionUrl = (APIanswer) => {
    const hasMultipleAdminURLs = APIanswer.urls.admin?.length > 1;

    if (!hasMultipleAdminURLs) {
        return APIanswer.urls.admin?.[0]?.url;
    }

    // This assumes it is always the second URL that is the password action URL.
    return APIanswer.urls.admin?.[1]?.url;
};

// =============================================================================
// THEME CREATION
// =============================================================================

const createBrandTheme = (themeDetails) => {
    const colorBlack = '#000000';
    const colorGreySuperDark = '#121212';
    const colorGreyDark = '#242424';
    const colorIconDark = '#3f4142';
    const colorIcon = '#474747';
    const colorGreyMid = '#727272';
    const colorGreyLight = '#ADADAD';
    const colorOrnament = '#D7D7D7';
    const colorBackgroundDark = '#ECECEC';
    const colorGreySuperLight = '#F8F8F8';
    const colorWhite = '#FFFFFF';

    return {
        palette: {
            primary: {
                main: themeDetails.brand.light,
                dark: themeDetails.brand.dark,
                contrastText: colorWhite,
            },
            flavors: {
                green: themeDetails.green.light,
                red: themeDetails.red.light,
                yellow: themeDetails.yellow.light,
                flavor1: themeDetails.flavor1.light,
                flavor2: themeDetails.flavor2.light,
                flavor3: themeDetails.flavor3.light,
                flavor4: themeDetails.flavor4.light,
                flavor5: themeDetails.flavor5.light,
                flavor6: themeDetails.flavor6.light,
                flavor7: themeDetails.flavor7.light,
                flavor8: themeDetails.flavor8.light,
                flavor9: themeDetails.flavor9.light,
            },
            greyscale: {
                backgroundDark: colorBackgroundDark,
                black: colorBlack,
                greyDark: colorGreyDark,
                greyLight: colorGreyLight,
                greyMid: colorGreyMid,
                greySuperDark: colorGreySuperDark,
                greySuperLight: colorGreySuperLight,
                icon: colorIcon,
                iconDark: colorIconDark,
                ornament: colorOrnament,
                white: colorWhite,
            },
            other: {
                link: themeDetails.link.light,
                meReference: themeDetails.chatYou.light,
                conference: themeDetails.pbxConference.light,
                ivr: themeDetails.pbxRefer.light,
                queue: themeDetails.pbxHuntgroup.light,
                channel: themeDetails.pbxChannel.light,
                voicemail: themeDetails.pbxVoicemail.light,
                connection: themeDetails.pbxConnection.light,
            },
            error: {
                main: themeDetails.red.light,
                contrastText: colorWhite,
            },
        },
    };
};

// =============================================================================
// BRAND CREATION
// =============================================================================

const createBrandFromApiBrand = (APIanswer) => {
    const themeDetails = APIanswer.theme;
    return Object.freeze({
        brandDomain: APIanswer.domain,
        brandKey: APIanswer.key,
        brandNameUIFriendly: APIanswer.displayName || APIanswer.description,
        brandAdminUrl: addHttpsIfNoProtocol(APIanswer.urls.admin?.[0]?.url), // Multiple admin URL support is a thing only Telavox needs, so we can just grab the first one.
        brandAppUrl: addHttpsIfNoProtocol(APIanswer.urls.flowApp.url),
        brandWallboardUrl: addHttpsIfNoProtocol(APIanswer.urls.dashboard.url),
        brandAppDownLoadUrls: addHttpsIfNoProtocol(
            APIanswer.urls.appDownload?.url,
        ),
        passwordActionUrl: addHttpsIfNoProtocol(
            getPasswordActionUrl(APIanswer),
        ),
        brandConfNumberUrl: addHttpsIfNoProtocol(
            APIanswer.urls.conferenceNumber.url,
        ),
        brandStatsApplicationUrl: addHttpsIfNoProtocol(
            APIanswer.urls.statsApplication?.url,
        ),
        // Blank in Partner comes back as "", which addHttpsIfNoProtocol turns into null.
        brandGeneralPrivacyPolicyUrl: addHttpsIfNoProtocol(
            APIanswer.urls.generalPrivacyPolicy?.url?.trim(),
        ),
        theme: createBrandTheme(themeDetails),
    });
};

// =============================================================================
// CONSTANTS - FALLBACK DATA
// =============================================================================

export const FALLBACK_THEME_DETAILS = Object.freeze({
    brand: { light: '#000000', dark: '#FFFFFF' },
    green: { light: '#2DC659', dark: '#2DC659' },
    red: { light: '#FD5234', dark: '#FD5234' },
    yellow: { light: '#FCD41E', dark: '#FCD41E' },
    link: { light: '#2C70B5', dark: '#2C70B5' },
    chatYou: { light: '#2C70B5', dark: '#2C70B5' },
    pbxConference: { light: '#25A6D1', dark: '#25A6D1' },
    pbxRefer: { light: '#79438C', dark: '#79438C' },
    pbxHuntgroup: { light: '#D54E8C', dark: '#D54E8C' },
    pbxChannel: { light: '#DC5E5E', dark: '#DC5E5E' },
    pbxVoicemail: { light: '#70CCD7', dark: '#70CCD7' },
    pbxConnection: { light: '#E6BD03', dark: '#E6BD03' },
    flavor1: { light: '#F17B3B', dark: '#F17B3B' },
    flavor2: { light: '#E6BD03', dark: '#E6BD03' },
    flavor3: { light: '#89CB55', dark: '#89CB55' },
    flavor4: { light: '#36BA9B', dark: '#36BA9B' },
    flavor5: { light: '#25A6D1', dark: '#25A6D1' },
    flavor6: { light: '#2C70B5', dark: '#2C70B5' },
    flavor7: { light: '#79438C', dark: '#79438C' },
    flavor8: { light: '#D54E8C', dark: '#D54E8C' },
    flavor9: { light: '#DC5E5E', dark: '#DC5E5E' },
    iconOnBrand: { light: '#FFFFFF', dark: '#000000' },
    textOnBrand: { light: '#FFFFFF', dark: '#000000' },
});

// Fallback logos that correspond to the fallback brand - all set to null
// to ensure no default logos are shown when brand data is not available
const FALLBACK_LOGOS = {
    favicon: null,
    faviconDarkMode: null,
    logoSmall: null,
    logoText: null,
};

const FALLBACK_BRAND = {
    brandNameUIFriendly: '',
    passwordActionUrl: null,
    brandAdminUrl: null,
    brandAppUrl: null,
    brandWallboardUrl: null,
    brandConfNumberUrl: null,
    brandStatsApplicationUrl: null,
    brandGeneralPrivacyPolicyUrl: null,
    theme: createBrandTheme(FALLBACK_THEME_DETAILS),
};

// =============================================================================
// PUBLIC API FUNCTIONS
// =============================================================================

export const getBrand = (useEnvironment = false) =>
    (useEnvironment ? ENVIRONMENT_BRAND : API_BRAND) || FALLBACK_BRAND;

export const getBrandLogo = (
    type,
    useEnvironment = false,
    isEnableGeoNex = false,
) => {
    let logos;
    if (useEnvironment) {
        logos = ENVIRONMENT_LOGOS || API_LOGOS;
    } else {
        logos = API_LOGOS || ENVIRONMENT_LOGOS;
    }

    // If logos is set and contains the requested type, return the full logo URL.
    const hasLogo = logos?.[type]?.url;

    if (hasLogo) {
        const logoUrlBase = logos[type]?.url;
        const basePath = isEnableGeoNex
            ? getGeoNexBaseUrl()
            : homeClient?.apiHost;

        if (basePath === null) {
            return FALLBACK_LOGOS[type] || null;
        }

        const logoUrl = isEnableGeoNex
            ? logoUrlBase.replace('internal/v2/', '')
            : logoUrlBase;

        return `${basePath}${logoUrl}`;
    }

    if (FALLBACK_LOGOS[type]) {
        return FALLBACK_LOGOS[type];
    }

    return null;
};

export const getBrandKey = () => {
    return getBrand()?.brandKey;
};

export const setDomainLogoUrls = () => {
    if (!API_LOGOS || !API_BRAND || !API_BRAND.brandDomain) return;

    // These are the logos that we have on the sign in page
    // When we sign out, we are no longer authorized.
    // Add domain to make sure we can fetch the logos.
    const allowedKeys = [
        'favicon',
        'faviconDarkMode',
        'logo48LightMode',
        'logo48DarkMode',
    ];
    allowedKeys.forEach((key) => {
        const logo = API_LOGOS[key];
        if (logo && logo.url) {
            // Replace "/branding/image/" with `/branding/${API_BRAND.brandDomain}/image/`
            logo.url = logo.url.replace(
                /\/branding\/image\//,
                `/branding/${API_BRAND.brandDomain}/image/`,
            );
        }
    });
};

export const resetToEnvironmentBranding = () => {
    if (ENVIRONMENT_BRAND) {
        API_BRAND = ENVIRONMENT_BRAND;
    }
    if (ENVIRONMENT_THEME) {
        updateBrandingTheme(ENVIRONMENT_THEME);
    }
    if (ENVIRONMENT_LOGOS) {
        API_LOGOS = ENVIRONMENT_LOGOS;
    }
};
